// WAITING FOR SIGN-OFF: the links still out - open, or expired and not yet replaced - with
// Resend (a fresh link, the old one withdrawn) and Withdraw for whoever may ask for a
// sign-off. The API had both since the start but no page called them, so a contractor's
// "Ask for a new link" reached the requester with nothing to press (2026-10-30).
import React, { useState } from "react";
import { Button, Popconfirm, Space, Tag } from "antd";
import moment from "moment";
import { resendSignoffLink, revokeSignoffLink } from "api/signoff";
import addNotification from "helpers/notify";

/** The links worth showing: open or expired, newest request first. */
export function waitingLinks(requests) {
  const out = [];
  for (const r of requests || []) {
    for (const l of r.links || []) {
      if (l.state === "open" || l.state === "expired") out.push({ request: r, link: l });
    }
  }
  return out.sort((a, b) => new Date(b.request.sentAt || 0) - new Date(a.request.sentAt || 0));
}

/** "1 of 2 owner/GC signed" when the blue star needs all of them, else null. */
export function blueProgress(r, l) {
  if (!r || r.rule !== "allOwnerGc" || l.grant !== "ownerGc") return null;
  const asked = (r.links || []).filter((x) => x.grant === "ownerGc" && x.state !== "revoked");
  return { signed: asked.filter((x) => x.outcome === "signed").length, of: asked.length };
}

function stateTag(l) {
  if (l.mailFailed) return <Tag color="red">mail failed</Tag>;
  if (l.state === "expired") return <Tag color={l.renewAskedAt ? "orange" : "default"}>{l.renewAskedAt ? "asked for a new link" : "link expired"}</Tag>;
  if (l.outcome === "changes") return <Tag color="orange">asked for changes</Tag>;
  return <Tag color="blue">expires {moment(l.expiresAt).fromNow()}</Tag>;
}

export default function OpenRequests({ projectId, requests, canRequest, onChanged, limit = 8 }) {
  const [busy, setBusy] = useState("");
  const rows = waitingLinks(requests);
  if (!rows.length) return null;
  const act = async (id, fn, done) => {
    setBusy(id);
    try {
      await fn(projectId, id);
      addNotification(done, "success");
      if (onChanged) onChanged();
    } catch (e) {
      addNotification((e && e.message) || "That did not go through", "danger");
    } finally { setBusy(""); }
  };
  return (
    <div className="signoff-box mt-2" data-testid="signoff-waiting">
      <div className="signoff-box-title">Waiting for sign-off <small className="signoff-muted">({rows.length})</small></div>
      {rows.slice(0, limit).map(({ request: r, link: l }) => (
        <div key={l.id} className="tile-row">
          <span>
            {l.name || l.email}{l.company ? ` · ${l.company}` : ""} <small className="signoff-muted">{{ tenant: "contractor", partner: "trade partner" }[l.grant] || "owner/GC"}{l.role ? ` · ${l.role}` : ""} · {r.phase}</small> {stateTag(l)}
            {blueProgress(r, l) ? <Tag>{`blue needs all: ${blueProgress(r, l).signed} of ${blueProgress(r, l).of} signed`}</Tag> : null}
          </span>
          {canRequest ? (
            <Space size={4}>
              <Button size="small" loading={busy === l.id} onClick={() => act(l.id, resendSignoffLink, `A new link went to ${l.email}`)}>Resend</Button>
              {l.state === "open" ? (
                <Popconfirm title={`Withdraw ${l.name || l.email}'s link?`} okText="Withdraw" onConfirm={() => act(l.id, revokeSignoffLink, "Link withdrawn")}>
                  <Button size="small" danger disabled={busy === l.id}>Withdraw</Button>
                </Popconfirm>
              ) : null}
            </Space>
          ) : null}
        </div>
      ))}
      {rows.length > limit ? <small className="signoff-muted">+{rows.length - limit} more</small> : null}
    </div>
  );
}
